import React, { useEffect, useState, useCallback } from "react";
import { ShieldCheck, Check, X, Eye } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";

export const IdChecksTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [imgUrl, setImgUrl] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/id-checks");
      setRows(r.data);
    } catch (e) { toast.error("Failed to load ID checks"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openImage = async (row) => {
    try {
      const r = await api.get(`/admin/id-checks/${row.id}/image`, { responseType: "blob" });
      setImgUrl(URL.createObjectURL(r.data));
      setViewing(row);
    } catch (e) { toast.error("Could not load ID image"); }
  };

  const review = async (row, action) => {
    try {
      await api.post(`/admin/id-checks/${row.id}/review`, { action, note: "" });
      toast.success(action === "approve" ? "ID approved — verified 21+" : "ID rejected");
      setViewing(null);
      if (imgUrl) { URL.revokeObjectURL(imgUrl); setImgUrl(null); }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Review failed"); }
  };

  const pending = rows.filter((r) => r.id_verification?.status === "pending");

  if (loading) return <p className="text-[var(--twr-warm-white)]/55">Loading…</p>;

  return (
    <div data-testid="admin-idchecks-tab">
      <h1 className="font-serif text-4xl">ID Checks</h1>
      <p className="text-[var(--twr-warm-white)]/55 mt-2">{pending.length} pending review · orders proceed immediately, review before shipping.</p>

      <div className="mt-8 overflow-x-auto surface">
        <table className="w-full text-sm">
          <thead className="border-b border-[var(--twr-line)] text-[var(--twr-warm-white)]/55 text-[11px] uppercase tracking-[0.18em]">
            <tr>
              <th className="text-left px-5 py-4">Name</th>
              <th className="text-left px-5 py-4">Email</th>
              <th className="text-left px-5 py-4">Uploaded</th>
              <th className="text-left px-5 py-4">Status</th>
              <th className="text-right px-5 py-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const idv = row.id_verification || {};
              return (
                <tr key={row.id} data-testid={`idcheck-row-${row.id}`} className="border-b border-[var(--twr-line)]/60 hover:bg-[var(--twr-bronze)]/5 transition-colors">
                  <td className="px-5 py-4">{row.full_name}</td>
                  <td className="px-5 py-4 text-[var(--twr-warm-white)]/75">{row.email}</td>
                  <td className="px-5 py-4 text-[var(--twr-warm-white)]/55 text-[12px]">{idv.uploaded_at ? new Date(idv.uploaded_at).toLocaleString("en-US", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                  <td className="px-5 py-4">
                    <span className={`text-[11px] tracking-[0.18em] uppercase ${idv.status === "pending" ? "text-amber-400" : idv.status === "approved" ? "text-emerald-400" : "text-red-400"}`}>{idv.status}</span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="inline-flex gap-2">
                      <button onClick={() => openImage(row)} data-testid={`idcheck-view-${row.id}`} title="View ID"
                        className="inline-flex items-center justify-center w-8 h-8 border border-[var(--twr-line-strong)] hover:border-[var(--twr-bronze)] hover:text-[var(--twr-bronze)] transition-colors"><Eye size={13}/></button>
                      {idv.status === "pending" && (
                        <>
                          <button onClick={() => review(row, "approve")} data-testid={`idcheck-approve-${row.id}`} title="Approve"
                            className="inline-flex items-center justify-center w-8 h-8 border border-[var(--twr-line-strong)] hover:border-emerald-400 hover:text-emerald-400 transition-colors"><Check size={13}/></button>
                          <button onClick={() => review(row, "reject")} data-testid={`idcheck-reject-${row.id}`} title="Reject"
                            className="inline-flex items-center justify-center w-8 h-8 border border-[var(--twr-line-strong)] hover:border-red-400 hover:text-red-400 transition-colors"><X size={13}/></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td className="px-5 py-10 text-center text-[var(--twr-warm-white)]/55" colSpan={5}>No IDs submitted yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => { if (!o) { setViewing(null); if (imgUrl) { URL.revokeObjectURL(imgUrl); setImgUrl(null); } } }}>
        <DialogContent className="bg-[#0A0A0A] border border-[var(--twr-line)] text-[var(--twr-warm-white)] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-left">{viewing?.full_name}</DialogTitle>
            <DialogDescription className="text-left text-[var(--twr-muted,#A1A1AA)]">Review the uploaded ID and confirm the customer is 21 or older.</DialogDescription>
          </DialogHeader>
          {imgUrl && <img src={imgUrl} alt="Uploaded ID" className="w-full max-h-[55vh] object-contain bg-black" data-testid="idcheck-image" />}
          {viewing?.id_verification?.status === "pending" && (
            <div className="flex gap-3 justify-end">
              <button onClick={() => review(viewing, "reject")} className="btn-ghost !border-red-400/50 !text-red-400">Reject</button>
              <button onClick={() => review(viewing, "approve")} className="btn-bronze" data-testid="idcheck-dialog-approve">Approve — Verified 21+</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
